import ColorInput from "@/components/common/form/inputs/ColorInput";
import { gql } from "@apollo/client";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import Input from "components/common/form/inputs/Input";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import PageForm, { PageFormProps } from "components/common/page/PageForm";
import RoomTypeDropdownInput, { FormRoomTypeType } from "components/entities/accommodation/roomType/RoomTypeDropdownInput";
import { uuid } from "config/app-constants";
import { useState } from "react";
import { BookingChannelFormDataQuery, CommissionTypes, SaveBookingChannelMutation, SaveBookingChannelMutationVariables } from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";

const query = gql`
query BookingChannelFormData($id: Int) {
	bookingChannel(id: $id) {
		commission
		type
		name
        color
		id
		roomTypeDefultPrices {
			items {
				roomType {
					id
					color
					name
				}
				defaultPrice
				id
			}
		}
	},
	roomTypes {
		items {
			name,
			defaultPrice,
			color
			id
		}
	}
	}
`;

const mutation = gql`
mutation SaveBookingChannel($model: BookingChannelInput!) {
	bookingChannel(model: $model) {
		id
	}
}
`;

type Model = {
    id: number,
    commission: number | string,
    name: string,
    type: CommissionTypes | null,
    color?: string,
    roomTypeDefultPrices: {
        id: number | string,
        roomTypeName: string,
        roomTypeId: number,
        defaultPrice: number | string,
    }[],
}

const BookingChannelFormPage = () => {
    const context = useAppContext();
    const [model, setModel] = useState<Model>({
        id: 0,
        commission: '',
        name: '',
        type: null,
        color: '',
        roomTypeDefultPrices: []
    });

    const pageConfig: PageFormProps<BookingChannelFormDataQuery, SaveBookingChannelMutation, SaveBookingChannelMutationVariables> = {
        title: 'pages.accommodation.channel.form',
        name: 'booking-channel-form',
        onSubmit: {
            resolveMutationData() {
                return {
                    model: {
                        commission: Number(model.commission),
                        id: model.id,
                        name: model.name,
                        type: model.type!,
                        color: model.color,
                        roomTypeDefultPrices: model.roomTypeDefultPrices.map(item => ({
                            id: Number(item.id) || 0,
                            defaultPrice: Number(item.defaultPrice),
                            roomTypeId: item.roomTypeId,
                            channelId: model.id || 0
                        }))
                    }
                };
            },
            mutation: mutation,
        },
        load: {
            query: query,
            resolveQueyData(data) {
                if (data?.bookingChannel) {
                    setModel({
                        commission: data.bookingChannel.commission,
                        id: data.bookingChannel.id,
                        name: data.bookingChannel.name,
                        type: data.bookingChannel.type,
                        color: data.bookingChannel.color || '',
                        roomTypeDefultPrices: data.bookingChannel.roomTypeDefultPrices.items.map(item => ({
                            id: item.id,
                            defaultPrice: item.defaultPrice,
                            roomTypeId: item.roomType.id,
                            roomTypeName: item.roomType.name
                        }))
                    });
                }
            }
        }
    }

    const onRoomTypeChange = (roomType: FormRoomTypeType) => {
        const newModel = { ...model };
        newModel.roomTypeDefultPrices.push({
            id: uuid(),
            defaultPrice: '',
            roomTypeId: roomType.id,
            roomTypeName: roomType.name
        });
        setModel(newModel);
    }

    const onDefaultPriceChange = (index: number, value: string) => {
        const newModel = { ...model };
        newModel.roomTypeDefultPrices[index].defaultPrice = value;
        setModel(newModel);
    }

    const onDefaultPriceDelete = (id: number | string) => {
        model.roomTypeDefultPrices = model.roomTypeDefultPrices.filter(item => item.id !== id);
        setModel({ ...model });
    }

    return (
        <PageForm {...pageConfig} >
            <fieldset>
                <Input
                    name="name"
                    label="fields.name.name"
                    required
                    value={model.name}
                    onChange={e => setModel({ ...model, name: e || '' })}
                />
                <ColorInput
                    name="color"
                    label="fields.color.name"
                    value={model.color}
                    onChange={e => setModel({ ...model, color: e })}
                />
                <DropdownInput
                    name="type"
                    label="fields.type.name"
                    required
                    dataSource={{ data: [CommissionTypes.Percent, CommissionTypes.Total] }}
                    getItemId={item => item as any}
                    view={{ inputView: item => item }}
                    value={model.type}
                    onChange={e => setModel({ ...model, type: e || null })}
                />
                <Input
                    name="commission"
                    label="fields.commission.name"
                    required
                    type="number"
                    value={model.commission?.toString()}
                    onChange={e => setModel({ ...model, commission: e || '' })}
                />
            </fieldset>
            <fieldset>
                {model.roomTypeDefultPrices.map((item, index) => {
                    return (
                        <Input
                            key={item.id}
                            type="number"
                            required
                            label={`${item.roomTypeName} ${context.t('fields.defaultPrice.name')}`}
                            value={item.defaultPrice?.toString() || ''}
                            onChange={v => onDefaultPriceChange(index, v || '')}
                            suffix={{
                                icon: faTrash,
                                name: 'delete',
                                onClick: () => onDefaultPriceDelete(item.id),
                                title: 'button.delete',
                                className: 'text-danger'
                            }}
                        />
                    )
                })}
                <div className="w-full border-b theme-border border-dashed"></div>
                <RoomTypeDropdownInput
                    name="roomType"
                    label="fields.roomType.name"
                    onChange={e => onRoomTypeChange(e!)}
                    filters={{
                        and: model.roomTypeDefultPrices.map(item => ({
                            id: {
                                neq: item.roomTypeId
                            }
                        }))
                    }}
                />
            </fieldset>
        </PageForm>
    );
};

export default BookingChannelFormPage;
